import { createContext, useContext, useEffect, useState } from "react";
import * as React from "react";
import axios from "axios";
import { Branch, useLocationConfiguration } from "./LocationConfiguration";
// import { ApplicationData } from "@tlc/ls2pac/app/ApplicationData";

interface BranchLimitContextType {
  branchLimits: BranchLimit[];
  isConfigLimited: boolean;
  displayBranchLimitControl: boolean;
  visibleBranches: BranchLimit[];
  configLimitedBranches: Branch[];
  updateLimitedBranches: (limitedBranches: BranchLimit["identifier"][]) => void;
}

// Create the Branch Limit Context
const BranchLimitContext = createContext<BranchLimitContextType>(null);

export const useBranchLimits = () => {
  const context = useContext(BranchLimitContext);

  if (!context) {
    throw new Error(
      "useBranchLimits must be used within a BranchLimitProvider"
    );
  }

  return context;
};

// This replaces branchLimitsUtil in ApplicationData
// eventually when the whole app is in React this can merge with BranchLimitProvider
export const BranchLimitsHolder = (() => {

  let isConfigLimited: boolean | null = null;
  let branchLimits: BranchLimit[] | null = null;
  let branchLimitsRequested: boolean = false;
  const subscribers: ((event: BranchLimiting) => void)[] = []; // Array to hold subscriber functions

  const setBranchLimits = (newBranchLimits: BranchLimit[]) => {
    branchLimits = [...newBranchLimits];
    // Notify all subscribers when the branch limits change
    subscribers.forEach((subscriber) => subscriber({
      branchLimits,
      isConfigLimited
    }));
  };

  const setSelectedBranchLimits = (
    limitedBranches: BranchLimit["identifier"][]
  ) => {
    const updatedBranchLimits = branchLimits.map((branch) => ({
      ...branch,
      selected: limitedBranches.includes(branch.identifier),
    }));
    setBranchLimits(updatedBranchLimits);
  };

  const subscribe = (subscriber: (branchLimits: BranchLimiting) => void) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);
    
    if(!branchLimitsRequested){
      branchLimitsRequested = true;
      fetchBranchLimits();
    }

    if(branchLimits){
      subscriber({
        branchLimits,
        isConfigLimited
      });
    }
 
    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }

      // if everyone has unsubscribed, clear the held data
      // this helps with unit tests
      if(subscribers.length === 0){
        branchLimits = null;
        isConfigLimited = null;
        branchLimitsRequested = false;
      }
    };
  };

  const fetchBranchLimits = () => {
    axios
      .get<BranchLimiting>("/branch/branchLimits")
      .then(({ data }) => {
        const { branchLimits: fetchedBranchLimits, isConfigLimited: fetchedConfigLimited } = data;
        isConfigLimited = fetchedConfigLimited;
        setBranchLimits(fetchedBranchLimits); // Update the branch limits
      })
      .catch((error) => console.error("Error getting Branch Limits", error));
  };

  return {
    setSelectedBranchLimits,
    subscribe,
  };
})();

export interface BranchLimit {
  displayName: string;
  longDescription: string;
  identifier: string;
  selected: boolean;
  hidden: boolean;
  displayInCopies: boolean;
  displayInBranchLimits: boolean;
  displayInPickupLocation: boolean;
}

export interface BranchLimiting {
  isConfigLimited: boolean;
  branchLimits: BranchLimit[];
}

// Branch Limit Context Provider Component
export const BranchLimitProvider = ({ children }: React.PropsWithChildren) => {
  const [branchLimits, setBranchLimits] = useState<BranchLimit[]>(null);
  const [isConfigLimited, setIsConfigLimited] = useState<boolean>(null);
  
  const visibleBranches = React.useMemo(() => (branchLimits || []).filter(
    (branch) => branch.hidden === false && branch.displayInCopies
  ), [branchLimits]);

  const { locationConfiguration: {filterBranches} = { filterBranches: null }} = useLocationConfiguration();
  const configLimitedBranches = React.useMemo(() => (filterBranches || []).filter((branch) => branch.shared = false), [filterBranches]);
  const displayBranchLimitControl = React.useMemo(() => visibleBranches.length > 1 && configLimitedBranches.length !== 1, [visibleBranches, configLimitedBranches]);

  const updateLimitedBranches = (
    limitedBranches: BranchLimit["identifier"][]
  ) => {
    //   const updatedBranchLimits = branchLimits.map(branch => ({
    //     ...branch,
    //     selected: limitedBranches.includes(branch.identifier)
    // }))
    //   setBranchLimits(updatedBranchLimits);
    BranchLimitsHolder.setSelectedBranchLimits(limitedBranches);
  };

  // Fetch branch limits on load
  useEffect(() => {
    const unsubscribe = BranchLimitsHolder.subscribe(({branchLimits, isConfigLimited}) => {
      setBranchLimits(branchLimits);


      setIsConfigLimited(isConfigLimited);
    });

    return () => {
      unsubscribe();
    }
  }, []);

  // Listen for Logout and clear current limits
  useEffect(() => {
    const handleLogout = () => {
      // Clear current limits
      // Example: setBranchLimits(null);
    };

    // Subscribe to logout event
    // Example: subscribeToEvent('logout', handleLogout);

    // Unsubscribe on component unmount
    return () => {
      // Unsubscribe from logout event
      // Example: unsubscribeFromEvent('logout', handleLogout);
    };
  }, []);

  // Listen for search and update branch filters if necessary
  useEffect(() => {
    // const handleSearch = (searchParams) => {
    // Update branch filters if necessary from search params
    // Example: updateBranchFilters(searchParams);
    // };

    // Subscribe to search event
    // Example: subscribeToEvent('search', handleSearch);

    // Unsubscribe on component unmount
    return () => {
      // Unsubscribe from search event
      // Example: unsubscribeFromEvent('search', handleSearch);
    };
  }, []);

  // Define the context value
  const contextValue = {
    branchLimits,
    visibleBranches,
    configLimitedBranches,
    isConfigLimited,
    updateLimitedBranches,
    displayBranchLimitControl
  };

  return (
    <BranchLimitContext.Provider value={contextValue}>
      {children}
    </BranchLimitContext.Provider>
  );
};
